import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Grid, Chip, IconButton, Button,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Divider,
  Autocomplete,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import Cid10Autocomplete from './Cid10Autocomplete';
import { useAuth } from '../../contexts/AuthContext';
import GuidanceButton from '../common/GuidanceButton';
import type { EvolutionAttachedPrintable } from '../evolution/evolutionWorkflowTypes';
import PrescriptionTemplateChipPicker from './PrescriptionTemplateChipPicker';
import {
  applyCertificateTemplateToForm,
  normalizeCertificateTemplate,
  resolveCertificateFormContent,
  syncCertificateFormContent,
} from '../../utils/certificateTemplateUtils';

const typeLabels: Record<string, string> = {
  medical_leave: 'Atestado Médico',
  attendance: 'Declaração de Comparecimento',
  fit_to_work: 'Aptidão ao Trabalho',
  accompaniment: 'Acompanhante',
  custom: 'Personalizado',
};

/** Soma N dias a uma data (YYYY-MM-DD). */
function addDays(dateStr: string, n: number): string {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
}

/** Tipos que exigem CID obrigatório (sugestão médica 29.05: atestado médico). */
const CID_REQUIRED_TYPES = ['medical_leave'];

function cleanFormData(data: any) {
  const type = data.certificate_type || 'medical_leave';
  // Título = nome do tipo (não editável — sugestão médica 29.05).
  const cleaned: any = {
    certificate_type: type,
    title: typeLabels[type] || 'Atestado',
    content: data.content || '',
    patient_id: data.patient_id || null,
    professional_id: data.professional_id || null,
    issue_date: data.issue_date || new Date().toISOString().split('T')[0],
    notes: data.notes || null,
  };
  if (data.cid_code) {
    cleaned.diagnosis_code = data.cid_code;
    cleaned.diagnosis = data.cid_description || data.cid_code;
    cleaned.show_diagnosis = true;
  }
  // Período de afastamento = nº de dias; datas calculadas a partir da emissão.
  if (data.days_off) {
    const days = Number(data.days_off) || 0;
    cleaned.leave_days = days;
    if (days > 0 && cleaned.issue_date) {
      cleaned.leave_start_date = cleaned.issue_date;
      cleaned.leave_end_date = addDays(cleaned.issue_date, days - 1);
    }
  }
  if (data.attendance_start_time) cleaned.attendance_start_time = data.attendance_start_time;
  if (data.attendance_end_time) cleaned.attendance_end_time = data.attendance_end_time;
  cleaned.content = resolveCertificateFormContent(data) || cleaned.content;
  // Acompanhante: nome + CPF (sugestão médica 29.05).
  if (type === 'accompaniment') {
    cleaned.companion_name = data.companion_name || null;
    cleaned.companion_cpf = data.companion_cpf || null;
  }
  Object.keys(cleaned).forEach((key) => {
    if (['title', 'content', 'certificate_type'].includes(key)) {
      if (cleaned[key] === null || cleaned[key] === undefined) {
        cleaned[key] = key === 'title' ? (typeLabels[cleaned.certificate_type] || 'Atestado') : '';
      }
    } else if (cleaned[key] === '' || cleaned[key] === undefined) {
      cleaned[key] = null;
    }
  });
  return cleaned;
}

/** Presets de período de afastamento (dias). */
const LEAVE_PRESETS = [1, 2, 3, 5, 7, 10, 15, 30, 60, 90];

export interface CertificateFormDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  professionalId: string;
  initialPatient?: any;
  initialProfessional?: any;
  prefillTemplate?: any | null;
  existingCertificateId?: string | null;
  existingClientKey?: string | null;
  onSuccess: (item: EvolutionAttachedPrintable) => void;
}

const CertificateFormDialog: React.FC<CertificateFormDialogProps> = ({
  open,
  onClose,
  patientId,
  professionalId,
  initialPatient,
  initialProfessional,
  prefillTemplate,
  existingCertificateId = null,
  existingClientKey = null,
  onSuccess,
}) => {
  const { user } = useAuth();
  // Paciente fixo (ficha aberta). Profissional travado no login, exceto perfis
  // administrativos que emitem por solicitação médica (sugestão médica 29.05).
  const canSelectProfessional = (user?.role as string) !== 'professional';
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [profSearch, setProfSearch] = useState('');
  const [profOptions, setProfOptions] = useState<any[]>([]);
  const lastProfSearchRef = useRef('');
  const loadSessionRef = useRef<string | null>(null);
  /** Evita resetar o formulário quando initialPatient/initialProfessional mudam após abrir o diálogo. */
  const openSessionRef = useRef(false);
  /** Autorização do CID do atestado em edição (null em atestado novo ou banco sem a coluna). */
  const [loadedCidConsent, setLoadedCidConsent] = useState<{ status: string; code: string } | null>(null);

  const [form, setForm] = useState<any>({
    certificate_type: 'medical_leave',
    content: '',
    content_template: '',
    patient_id: '',
    professional_id: '',
    issue_date: new Date().toISOString().split('T')[0],
    days_off: '',
    cid_code: '',
    cid_description: '',
    attendance_start_time: '',
    attendance_end_time: '',
    companion_name: '',
    companion_cpf: '',
    notes: '',
  });

  const patchForm = (patch: Record<string, unknown>) => {
    setForm((f: any) => {
      const next = { ...f, ...patch };
      const patient = initialPatient || patientOptions.find((p) => p.id === next.patient_id);
      const prof = initialProfessional || profOptions.find((p) => p.id === next.professional_id);
      return syncCertificateFormContent(next, patient, prof);
    });
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await axios.get(getApiUrl('/clinic/certificates/templates'));
        setTemplates((data?.data || []).map(normalizeCertificateTemplate));
      } catch {
        setTemplates([]);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) {
      openSessionRef.current = false;
      loadSessionRef.current = null;
      return;
    }
    if (openSessionRef.current) return;
    openSessionRef.current = true;

    const bootstrap = async () => {
      if (existingCertificateId && loadSessionRef.current !== existingCertificateId) {
        try {
          const { data } = await axios.get(getApiUrl(`/clinic/certificates/${existingCertificateId}`));
          const cert = data?.data ?? data;
          loadSessionRef.current = existingCertificateId;
          setLoadedCidConsent(
            cert && Object.prototype.hasOwnProperty.call(cert, 'cid_consent_status')
              ? { status: String(cert.cid_consent_status || 'not_requested'), code: String(cert.diagnosis_code || '') }
              : null,
          );
          setForm({
            certificate_type: cert?.certificate_type || 'medical_leave',
            content: cert?.content || '',
            content_template: '',
            patient_id: cert?.patient_id || patientId || '',
            professional_id: cert?.professional_id || professionalId || '',
            issue_date: cert?.issue_date?.split?.('T')?.[0] || cert?.issue_date || new Date().toISOString().split('T')[0],
            days_off: cert?.leave_days != null ? String(cert.leave_days) : '',
            cid_code: cert?.diagnosis_code || '',
            cid_description: cert?.diagnosis || '',
            attendance_start_time: cert?.attendance_start_time || '',
            attendance_end_time: cert?.attendance_end_time || '',
            companion_name: cert?.companion_name || '',
            companion_cpf: cert?.companion_cpf || '',
            notes: cert?.notes || '',
          });
          setPatientOptions(initialPatient ? [initialPatient] : []);
          setProfOptions(initialProfessional ? [initialProfessional] : []);
        } catch (e) {
          console.error(e);
        }
        return;
      }

      setLoadedCidConsent(null);
      setForm({
        certificate_type: prefillTemplate?.certificate_type || 'medical_leave',
        content: prefillTemplate?.content || '',
        content_template: '',
        patient_id: patientId || '',
        professional_id: professionalId || '',
        issue_date: new Date().toISOString().split('T')[0],
        days_off: '',
        cid_code: '',
        cid_description: '',
        attendance_start_time: '',
        attendance_end_time: '',
        companion_name: '',
        companion_cpf: '',
        notes: '',
      });
      setPatientOptions(initialPatient ? [initialPatient] : []);
      setProfOptions(initialProfessional ? [initialProfessional] : []);
      setProfSearch('');
      lastProfSearchRef.current = '';
      if (prefillTemplate) {
        setForm((f: any) => applyCertificateTemplateToForm(f, prefillTemplate, initialPatient, initialProfessional));
      }
    };

    void bootstrap();
  }, [open, patientId, professionalId, initialPatient, initialProfessional, prefillTemplate, existingCertificateId]);

  // Paciente é fixo (ficha aberta): não buscamos lista de pacientes.

  useEffect(() => {
    if (!open || !canSelectProfessional) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(getApiUrl('/clinic/budgets-search/professionals'), { params: { search: profSearch || '' } });
        if (data?.success !== false) setProfOptions(data?.data || []);
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [profSearch, open, canSelectProfessional]);

  const [error, setError] = useState('');

  const cidRefused = loadedCidConsent?.status === 'refused';
  const cidRequired = CID_REQUIRED_TYPES.includes(form.certificate_type) && !cidRefused;
  const cidChangeInvalidatesConsent = Boolean(
    loadedCidConsent
      && ['signed', 'pending'].includes(loadedCidConsent.status)
      && loadedCidConsent.code.trim().toUpperCase() !== String(form.cid_code || '').trim().toUpperCase()
      && String(form.cid_code || '').trim(),
  );

  const handleSave = async () => {
    if (cidRequired && !form.cid_code?.trim()) {
      setError('CID é obrigatório para Atestado Médico.');
      return;
    }
    if (form.certificate_type === 'accompaniment' && (!form.companion_name?.trim() || !form.companion_cpf?.trim())) {
      setError('Informe nome e CPF do acompanhante.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const cleanedData = cleanFormData(form);
      const res = existingCertificateId
        ? await axios.put(getApiUrl(`/clinic/certificates/${existingCertificateId}`), cleanedData)
        : await axios.post(getApiUrl('/clinic/certificates'), cleanedData);
      const saved = res.data?.data ?? res.data;
      const newId = saved?.id ?? saved?.certificate?.id ?? existingCertificateId;
      const title = typeLabels[form.certificate_type] || 'Atestado';
      const preview = String(form.content || '').slice(0, 500);
      const printable: EvolutionAttachedPrintable = {
        clientKey: existingClientKey || crypto.randomUUID(),
        category: 'certificate',
        templateId: prefillTemplate?.id,
        templateName: title,
        bodyText: preview + (String(form.content || '').length > 500 ? '…' : ''),
        linkedDocumentId: newId,
        linkedDocumentType: 'certificate',
      };
      onSuccess(printable);
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleReorderTemplates = async (orderedIds: string[]) => {
    try {
      await axios.put(getApiUrl('/clinic/certificates/templates/reorder'), { ids: orderedIds });
      setTemplates((prev) => {
        const byId = new Map(prev.map((t) => [String(t.id), t]));
        return orderedIds.map((id, index) => {
          const row = byId.get(String(id));
          return row ? { ...row, sort_order: index } : null;
        }).filter(Boolean) as any[];
      });
    } catch {
      try {
        const { data } = await axios.get(getApiUrl('/clinic/certificates/templates'));
        setTemplates((data?.data || []).map(normalizeCertificateTemplate));
      } catch { /* ignore */ }
    }
  };

  const applyTemplate = (template: any) => {
    setForm((f: any) => {
      const patient = initialPatient || patientOptions.find((p) => p.id === f.patient_id);
      const prof = initialProfessional || profOptions.find((p) => p.id === f.professional_id);
      return applyCertificateTemplateToForm(f, template, patient, prof);
    });
  };

  const isAccompaniment = form.certificate_type === 'accompaniment';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Novo atestado</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <GuidanceButton title="Atestados">
            Mesmo cadastro da página <strong>Atestados</strong>. Ao salvar, o documento é registrado e
            pode ser impresso como na central de atestados.
          </GuidanceButton>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {!prefillTemplate && templates.length > 0 && (
          <>
            <PrescriptionTemplateChipPicker
              templates={templates}
              onSelect={applyTemplate}
              onReorder={handleReorderTemplates}
              title="Modelos pré-preenchidos"
            />
            <Divider sx={{ mb: 2.5 }} />
          </>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select value={form.certificate_type} label="Tipo" onChange={(e) => setForm({ ...form, certificate_type: e.target.value })}>
                {Object.entries(typeLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Paciente"
              value={(patientOptions.find((p: any) => p.id === form.patient_id)?.full_name) || initialPatient?.full_name || ''}
              disabled
              helperText="Paciente da ficha aberta"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {canSelectProfessional ? (
              <Autocomplete
                size="small"
                options={profOptions}
                getOptionLabel={(o: any) => `${o.full_name || ''} — ${o.registration_type || 'CRM'} ${o.registration_number || ''}`}
                onInputChange={(_, v, reason) => {
                  if (reason !== 'reset') setProfSearch(v);
                }}
                onChange={(_, v) => {
                  setForm({ ...form, professional_id: v?.id || '' });
                  if (v) {
                    lastProfSearchRef.current = '';
                    setProfSearch('');
                  }
                }}
                value={profOptions.find((p: any) => p.id === form.professional_id) || null}
                renderInput={(params) => <TextField {...params} label="Profissional" helperText="Emissão por solicitação médica" />}
                isOptionEqualToValue={(o: any, v: any) => o?.id === v?.id}
                filterOptions={(x) => x}
                noOptionsText="Nenhum profissional encontrado"
              />
            ) : (
              <TextField
                fullWidth
                size="small"
                label="Profissional"
                value={(profOptions.find((p: any) => p.id === form.professional_id)?.full_name) || initialProfessional?.full_name || ''}
                disabled
                helperText="Profissional do login"
              />
            )}
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="date" label="Data de emissão" value={form.issue_date}
              onChange={(e) => patchForm({ issue_date: e.target.value })} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Cid10Autocomplete
              label="CID"
              required={cidRequired}
              value={form.cid_code}
              description={form.cid_description}
              onChange={(code, desc) => patchForm({ cid_code: code, cid_description: desc })}
            />
            {(cidRefused || cidChangeInvalidatesConsent) && (
              <Typography variant="caption" sx={{ color: cidRefused ? 'error.main' : 'warning.dark', display: 'block', mt: 0.5 }}>
                {cidRefused
                  ? 'O paciente não autorizou o CID neste atestado.'
                  : 'Trocar o CID exige nova autorização do paciente.'}
              </Typography>
            )}
          </Grid>
          {(form.certificate_type === 'attendance' || isAccompaniment) && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="time" label="Horário de início" value={form.attendance_start_time}
                  onChange={(e) => patchForm({ attendance_start_time: e.target.value })}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="time" label="Horário de fim" value={form.attendance_end_time}
                  onChange={(e) => patchForm({ attendance_end_time: e.target.value })}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
            </>
          )}
          {isAccompaniment && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" required label="Nome do acompanhante *" value={form.companion_name}
                  onChange={(e) => setForm({ ...form, companion_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" required label="CPF do acompanhante *" value={form.companion_cpf}
                  onChange={(e) => setForm({ ...form, companion_cpf: e.target.value })} />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.5 }}>
              Período de afastamento
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mb: 1 }}>
              {LEAVE_PRESETS.map((d) => (
                <Chip
                  key={d}
                  label={`${d} ${d === 1 ? 'dia' : 'dias'}`}
                  size="small"
                  onClick={() => patchForm({ days_off: String(d) })}
                  variant={String(form.days_off) === String(d) ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600, ...(String(form.days_off) === String(d) ? { bgcolor: '#3b82f6', color: '#fff' } : { borderColor: '#99f6e4', color: '#1d4ed8' }) }}
                />
              ))}
            </Box>
            <TextField sx={{ maxWidth: 240 }} fullWidth size="small" type="number" label="Dias de afastamento" value={form.days_off}
              onChange={(e) => patchForm({ days_off: e.target.value })}
              inputProps={{ min: 0 }} helperText="Clique num preset ou digite os dias" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={6} label="Conteúdo do atestado" value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value, content_template: '' })}
              placeholder="Atesto para os devidos fins que..."
              helperText={form.content_template ? 'Vinculado ao modelo — dias, datas e horários atualizam o texto automaticamente.' : undefined} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline rows={2} label="Observações" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Grid>
        </Grid>
        {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}>
          {saving ? <CircularProgress size={22} /> : 'Salvar e anexar à evolução'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CertificateFormDialog;
